import React, { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Box, Button, IconButton, useTheme } from "@mui/material";
import { BOTTOM_SHEET_Z_INDEX, Modal } from "@rentablo/components";
import SettingsIcon from "@icons/myicons/settings.svg?react";

interface Props {
  // Shows a dot on the button while a filter or the sort differs from its default.
  hasChanges?: boolean;
  // Whether anything in the panel differs from its default, chip order included.
  canReset?: boolean;
  onReset?: () => void;
  // The settings for the active tab, shown in the panel.
  children: ReactNode;
}

export const MobileListSettingsButton = ({
  hasChanges,
  canReset = false,
  onReset,
  children,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const settingsLabel = t(
    "domains.investments.investmentTable.mobile.settings"
  );
  const handleClose = () => setIsOpen(false);

  return (
    <>
      <Badge
        color="primary"
        variant="dot"
        invisible={!hasChanges}
        data-testid="mobile-table-settings-badge"
        // Centres the dot 8px in from the corner, inside the button.
        sx={{ "& .MuiBadge-badge": { top: 8, right: 8 } }}
      >
        <IconButton
          size="small"
          aria-label={settingsLabel}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          data-testid="mobile-table-settings-button"
          onClick={() => setIsOpen(true)}
        >
          <SettingsIcon width="20px" height="20px" />
        </IconButton>
      </Badge>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={settingsLabel}
        testId="mobile-table-settings"
        position="bottom"
        width="100%"
        height="fit-content"
        zIndex={BOTTOM_SHEET_Z_INDEX}
        disablePortal={false}
        contentPadding={theme.spacing(4, 0, 0)}
        titleMargin={2}
        outerSx={{ ".modal-heading-container": { px: 4 } }}
      >
        {children}
        {/* Settings apply right away; the button gives the sheet a clear way
            out and stays in view while the sheet scrolls. */}
        <Box
          position="sticky"
          bottom={0}
          display="flex"
          gap={3}
          px={4}
          py={4}
          bgcolor="background.paper"
        >
          {onReset && (
            <Button
              variant="tertiary"
              fullWidth
              disabled={!canReset}
              data-testid="mobile-table-settings-reset"
              onClick={() => {
                onReset();
                handleClose();
              }}
            >
              {t("domains.investments.investmentTable.mobile.reset")}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            data-testid="mobile-table-settings-accept"
            onClick={handleClose}
          >
            {t("domains.investments.investmentTable.mobile.accept")}
          </Button>
        </Box>
      </Modal>
    </>
  );
};
